import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Stack, Link, Typography } from '@mui/material';
import { PaymentsOutlined } from '@mui/icons-material';
import {
  Text,
  useGlobalization,
  useStyles,
  openDrawer,
  Button,
  error,
  success,
  rem,
  WidgetError,
} from '@innovyze/stylovyze';
import { CreateCostTableDialog } from '@Components/CreateCostTableDialog/';
import CostTablesListGrid from '@Components/CostTablesListGrid';
import HelpDrawer, { HELP_DRAWER } from '@Components/HelpDrawer';
import { CostTableRequestData } from '@Types/costTable.types';
import { FullPageWrapper } from '@Utils/styles';
import {
  newCostTable,
  copyCostTable,
  deleteCostTable,
} from '@Services/costTables.services';
import { useCostTablesOverview } from '@Hooks/useCostTableSelectors';
import {
  listCostTablesAction,
  invalidateCostTablesOverview,
} from '@Actions/costTables.actions';
import { COST_TABLES_REQUEST } from '@Utils/costTableRequestParams';
import { listGroupSets } from '@Actions/groupSets.actions';
import { selectGroupSetsStatus } from '@Selectors/groupSets.selector';
import { useCostTableTracking } from '@Hooks/useCostTableTracking';
import { useHasEditAccess } from '@Hooks/useHasEditAccess';

const CostTableOverview = () => {
  const { t } = useGlobalization();
  const { themePalette } = useStyles();
  const themeIconColor = themePalette.primary.dark;
  const dispatch = useDispatch();
  const history = useHistory();
  const hasEditAccess = useHasEditAccess();

  const [openCostTableDefinitionDialog, setOpenCostTableDefinitionDialog] =
    useState<boolean>(false);

  const costTableOverviewData = useCostTablesOverview();
  const groupSetsStatus = selectGroupSetsStatus();

  const {
    trackCreateSuccess,
    trackCreateFailed,
    trackCopySuccess,
    trackCopyFailed,
    trackDeleteSuccess,
    trackDeleteFailed,
  } = useCostTableTracking();

  // Intentionally run once on mount. The guard values (successful, groupSetsStatus)
  // are read from the Redux store at mount time — if data was already loaded in a
  // prior session (e.g. navigating back from a detail page) the dispatches are skipped.
  useEffect(() => {
    if (costTableOverviewData.successful === null) {
      dispatch(listCostTablesAction(COST_TABLES_REQUEST));
    }
    if (groupSetsStatus === null) {
      dispatch(listGroupSets());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires once on mount to load initial data; adding deps would re-fetch on every status change
  }, []);

  const handleRefresh = () => {
    dispatch(listCostTablesAction(COST_TABLES_REQUEST));
    dispatch(listGroupSets());
  };

  const handleCreateCostTable = async (request: CostTableRequestData) => {
    try {
      const response = await newCostTable({
        assetGroupSetId: request.assetGroupSetId,
        name: request.name,
        costPer: request.costPer,
      });
      setOpenCostTableDefinitionDialog(false);
      dispatch(invalidateCostTablesOverview());
      dispatch(
        success(
          t('Cost Table created. Click on unit cost cells to assign costs.')
        )
      );
      trackCreateSuccess({
        costtableid: response.data.body.cost_table_id,
        assetgroupsetid: request.assetGroupSetId,
        costper: request.costPer,
      });
      history.push(`/cost-tables/${response.data.body.cost_table_id}`);
    } catch (err: any) {
      console.error('Error creating cost table:', err);
      dispatch(error(t('Unable to create cost table')));
      trackCreateFailed({ error: String(err?.message ?? err) });
      throw err;
    }
  };

  const handleTableCopy = async (id: string) => {
    if (!id) return;
    try {
      const response = await copyCostTable({ cost_table_id: id });
      dispatch(invalidateCostTablesOverview());
      dispatch(success(response.data.body.message));
      trackCopySuccess({
        sourcecosttableid: id,
        newcosttableid: response.data.body.new_cost_table_id,
      });
      history.push(`/cost-tables/${response.data.body.new_cost_table_id}`);
    } catch (err: any) {
      console.error('Error copying cost table:', err);
      dispatch(error(t('Unable to copy cost table')));
      trackCopyFailed({
        sourcecosttableid: id,
        error: String(err?.message ?? err),
      });
    }
  };

  const handleTableDelete = async (id: string, name: string) => {
    if (!id) return;
    try {
      await deleteCostTable({ cost_table_id: id });
      dispatch(success(t('Cost table "{{name}}" deleted', { name })));
      dispatch(listCostTablesAction(COST_TABLES_REQUEST));
      trackDeleteSuccess({ costtableid: id });
    } catch (err: any) {
      console.error('Error deleting cost table:', err);
      dispatch(error(t('Unable to delete cost table')));
      trackDeleteFailed({
        costtableid: id,
        error: String(err?.message ?? err),
      });
    }
  };

  const pageSubHeader = (
    <Typography
      variant="body2"
      color="text.secondary"
      data-cy="page-header-sub">
      {t(
        'Manage unit costs per asset group for rehab actions such as replacement, repair or inspection.'
      )}{' '}
      <Link
        style={{ cursor: 'pointer' }}
        onClick={() => dispatch(openDrawer(HELP_DRAWER.COST_TABLES))}>
        {t('Learn more about cost tables.')}
      </Link>
    </Typography>
  );

  const emptyView = (
    <Stack
      alignItems="center"
      justifyContent="flex-start"
      spacing={3}
      style={{
        padding: `${rem(96)} ${rem(24)} ${rem(48)}`,
        textAlign: 'center',
        height: '100%',
        width: '100%',
      }}
      data-testid="cost-table-empty-state">
      <Stack
        alignItems="center"
        justifyContent="center"
        style={{ height: 72, width: 72 }}
        aria-hidden="true">
        <PaymentsOutlined
          sx={{
            color: themeIconColor,
            height: '3em',
            width: '3em',
          }}
        />
      </Stack>
      <Stack spacing={1} maxWidth={575}>
        <Text variant={'heading-large'} style={{ marginBottom: rem(4) }}>
          {t('Create Cost Table')}
        </Text>
        <div
          style={{
            color: 'rgba(0,0,0,0.60)',
            fontSize: rem(14),
            lineHeight: rem(20),
          }}>
          {t(
            'Define how much each rehab action costs per asset group. Choose an asset group set (e.g. by diameter and material), then set unit costs for each combination. You can later assign these tables to actions in a decision tree to calculate total costs across your network.'
          )}
        </div>
      </Stack>
      <Stack alignItems="center" spacing={1}>
        {hasEditAccess && (
          <Button
            variant="contained"
            color="primary"
            disabled={false}
            onClick={() => setOpenCostTableDefinitionDialog(true)}>
            {t('Create Cost Table')}
          </Button>
        )}
        <Link
          onClick={() => history.push('/agsets')}
          style={{ cursor: 'pointer' }}>
          {t('Go to Asset Group')}
        </Link>
      </Stack>
    </Stack>
  );

  const errorView = (
    <Stack
      spacing={2}
      alignItems="center"
      justifyContent="center"
      style={{ padding: '48px 24px' }}>
      <WidgetError message={t('Failed to load cost tables')} />
      <Button variant="contained" color="primary" onClick={handleRefresh}>
        {t('Retry')}
      </Button>
    </Stack>
  );

  const viewWithCostData = (
    <FullPageWrapper
      title={t('Cost Tables')}
      breadcrumbs={[{ title: t('Analysis'), link: '/analysis' }]}
      subHeader={pageSubHeader}
      headerUnderline
      applyPadding={true}>
      {costTableOverviewData.successful === false ? (
        errorView
      ) : (
        <CostTablesListGrid
          setCreateDialogOpen={setOpenCostTableDefinitionDialog}
          costTableListData={costTableOverviewData}
          onRefresh={handleRefresh}
          handleTableCopy={handleTableCopy}
          handleTableDelete={handleTableDelete}
        />
      )}
    </FullPageWrapper>
  );

  const emptyStateView = (
    <FullPageWrapper
      title={t('Cost Tables')}
      breadcrumbs={[{ title: t('Analysis'), link: '/analysis' }]}
      subHeader={pageSubHeader}
      headerUnderline
      applyPadding={true}>
      {emptyView}
    </FullPageWrapper>
  );

  // totalCount reflects the unfiltered API total (we always load with search_value: null),
  // so a client-side search that returns 0 matches never triggers this — the grid shows
  // its own "no rows" state instead. No need for a search-value guard here.
  const shouldShowEmptyState =
    !costTableOverviewData.isLoading && costTableOverviewData.totalCount === 0;

  return (
    <>
      {shouldShowEmptyState ? emptyStateView : viewWithCostData}
      <CreateCostTableDialog
        isOpen={openCostTableDefinitionDialog}
        setOpen={setOpenCostTableDefinitionDialog}
        onCreate={handleCreateCostTable}
      />
      <HelpDrawer drawerID={HELP_DRAWER.COST_TABLES} />
    </>
  );
};

export default CostTableOverview;
